export const enGB = {
  lang: 'en-GB',
  country: 'UK',
  flag: '🇬🇧',
  currency: '£',

  app: {
    sidebar: {
      dashboard: 'Dashboard',
      transactions: 'Transactions',
      pots: 'Pots',
      receipts: 'Receipts',
      reports: 'Reports',
      categories: 'Categories',
      tags: 'Tags',
      budgets: 'Budgets',
      recurring: 'Recurring',
      settings: 'Settings',
      darkMode: 'Dark Mode',
      lightMode: 'Light Mode',
      logout: 'Logout',
    },
    common: {
      loading: 'Loading...',
      save: 'Save',
      cancel: 'Cancel',
      confirmTitle: 'Confirm',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      search: 'Search',
      filter: 'Filters',
      export: 'Export',
      type: 'Type',
      category: 'Category',
      date: 'Date',
      amount: 'Amount',
      description: 'Description',
      actions: 'Actions',
      income: 'Income',
      expense: 'Expense',
      name: 'Name',
      color: 'Colour',
      confirmDelete: 'Are you sure you want to delete?',
      noResults: 'No results found',
      status: 'Status',
      error: 'Error',
      success: 'Success',
      page: 'Page',
      of: 'of',
      showing: 'Showing',
      viewDetails: 'View details',
      tags: 'Tags',
      receipt: 'Receipt',
      receiptAttached: 'Receipt attached',
    },
    categoryLabels: {
      food: 'Food',
      transport: 'Transport',
      shopping: 'Shopping',
      entertainment: 'Entertainment',
      health: 'Health',
      education: 'Education',
      utilities: 'Utilities',
      housing: 'Housing',
      travel: 'Travel',
      other: 'Other',
      salary: 'Salary',
      investment: 'Investment',
      freelance: 'Freelance',
      gift: 'Gift',
    },
    tags: {
      loading: 'Loading tags...',
      error: 'Could not load your tags. Please try again.',
      title: 'Tags',
      subtitle: 'Create tags to organise and filter your transactions',
      newTag: 'New Tag',
      noTags: 'No tags created',
      noTagsDescription: 'Tags are created automatically when you use #tag in your messages, or you can create them manually here.',
      createFirst: 'Create first tag',
      howToUse: 'How to use tags',
      howToUseDescription: 'Add tags to your transactions via WhatsApp using #tag. For example: "Lunch 35 pounds #work #lunch". Tags help you filter and analyse your expenses.',
      form: {
        name: 'Tag name',
        namePlaceholder: 'work, travel, lunch...',
        nameHint: 'Will be converted to lowercase without spaces',
        color: 'Colour',
      },
      stats: {
        title: 'Statistics',
        created: 'Tags created',
        totalUsage: 'Total uses',
        mostUsed: 'Most used',
        unused: 'Unused',
      },
      confirmDelete: 'Delete Tag',
      deleteWarning: 'Are you sure you want to delete the tag',
      deleteUsageWarning: 'This tag is being used in {count} transactions. The tag will be removed from these transactions.',
    },
    pots: {
      title: 'Pots',
      subtitle: 'Organise your money in different pots by currency',
      loading: 'Loading pots...',
      error: 'Could not load your pots. Please try again.',
      errorTitle: 'Failed to load pots',
      newPot: 'New Pot',
      editPot: 'Edit Pot',
      noPots: 'No pots created',
      noPotsDescription: 'Create pots to organise your money by currency or goal.',
      createFirst: 'Create first pot',
      setDefault: 'Set as default',
      transactions: 'transactions',
      confirmDelete: 'Delete Pot',
      confirmDeleteMessage: 'Are you sure you want to delete the pot',
      deleteWarning: 'This pot has {count} transactions that will be moved to the default pot.',
      form: {
        name: 'Name',
        namePlaceholder: 'E.g.: Emergency fund',
        description: 'Description (optional)',
        descriptionPlaceholder: 'What is this pot for?',
        currency: 'Currency',
        color: 'Colour',
      },
    },
    transactions: {
      loading: 'Loading transactions...',
      title: 'Transactions',
      subtitle: 'Manage your income and expenses',
      searchPlaceholder: 'Search by description, merchant...',
      showing: 'Showing {count} of {total} transactions',
      confirmDelete: 'Are you sure you want to delete this transaction?',
      filters: {
        filters: 'Filters',
        dateStart: 'Start Date',
        dateEnd: 'End Date',
        startDate: 'Start Date',
        endDate: 'End Date',
        minAmount: 'Min Amount',
        maxAmount: 'Max Amount',
      },
      table: {
        description: 'Description',
        category: 'Category',
        date: 'Date',
        amount: 'Amount',
        receipt: 'Receipt',
        actions: 'Actions',
      },
      viewReceipt: 'View receipt',
      modal: {
        editTitle: 'Edit Transaction',
        detailsTitle: 'Transaction Details',
        merchant: 'Merchant',
        description: 'Description',
        tags: 'Tags',
        receipt: 'Receipt',
        receiptAttached: 'Receipt attached',
        createCategory: 'Create new category',
      },
    },
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Overview of your finances',
      period: {
        week: 'Week',
        month: 'Month',
        year: 'Year',
      },
      cards: {
        income: 'Income',
        expense: 'Expenses',
        expenses: 'Expenses',
        balance: 'Balance',
        transactions: 'Transactions',
      },
      charts: {
        spending: 'Spending by Category',
        trend: 'Trend',
        byCategory: 'Expenses by Category',
      },
      recent: 'Recent Transactions',
      viewAll: 'View all',
      topCategories: 'Top Categories',
      items: 'items',
      loading: 'Loading dashboard...',
    },
    settings: {
      title: 'Settings',
      subtitle: 'Manage your profile and preferences',
      profile: {
        title: 'Profile',
        phone: 'Phone',
        phoneVerified: 'Verified',
        phoneHint: 'Phone is used for authentication and cannot be changed here.',
        name: 'Name',
        namePlaceholder: 'Your name',
        email: 'Email',
        emailPlaceholder: 'your@email.com',
        emailHint: 'Used to receive reports and notifications.',
        verified: 'Verified',
      },
      preferences: {
        title: 'Preferences',
        language: 'Language',
        languageHint: 'Language used in WhatsApp messages and reports.',
        timezone: 'Timezone',
      },
      subscription: {
        title: 'Subscription',
        plan: 'Plan',
        status: {
          active: 'Active',
          trialing: 'Trial',
          pastDue: 'Past Due',
          past_due: 'Past Due',
          canceled: 'Cancelled',
          expired: 'Expired',
        },
        trialEnds: 'Trial ends on',
        trialEndsAt: 'Trial ends on',
        nextBilling: 'Next billing',
        upgrade: 'Upgrade',
        support: 'Contact Support',
        contactSupport: 'Contact Support',
        cancel: 'Cancel subscription',
        noSubscription: 'No active subscription',
        noSubscriptionDesc: 'Subscribe to a plan to unlock all features.',
        viewPlans: 'View Plans',
        plans: {
          free: 'Free',
          basic: 'Basic',
          starter: 'Starter',
          pro: 'Pro',
          professional: 'Professional',
          enterprise: 'Enterprise',
          vip: 'VIP',
        },
      },
      account: {
        title: 'Account Information',
        id: 'Account ID',
        createdAt: 'Account created on',
      },
      saveChanges: 'Save Changes',
      loading: 'Loading settings...',
      error: 'Error loading settings',
      errorDesc: 'Could not load your information. Please try again.',
    },
    budgets: {
      title: 'Budgets',
      subtitle: 'Set spending limits by category',
      loading: 'Loading budgets...',
      error: 'Error loading budgets',
      errorDesc: 'Could not load your budgets. Please try again.',
      newBudget: 'New Budget',
      noBudgets: 'No budgets set',
      noBudgetsDesc: 'Create budgets to control your spending by category and receive alerts when approaching the limit.',
      createFirst: 'Create first budget',
      period: {
        monthly: 'Monthly',
        yearly: 'Yearly',
      },
      status: {
        safe: 'Within budget',
        warning: 'Near limit',
        danger: 'Limit exceeded!',
      },
      spent: 'spent',
      limit: 'Limit',
      remaining: 'Remaining',
      exceeded: 'Exceeded',
      summary: {
        totalBudgeted: 'Total Budgeted',
        totalSpent: 'Total Spent',
        overBudget: 'Over Budget',
        budget: 'budget',
        budgets: 'budgets',
      },
      tips: {
        title: 'Tips for good budgeting',
        tip1: 'Start with categories where you spend most',
        tip2: 'Set realistic limits based on your history',
        tip3: 'Use the 80% alert to act before exceeding the limit',
        tip4: 'Review your budgets monthly and adjust as needed',
      },
      confirmDelete: 'Delete Budget',
      confirmDeleteMsg: 'Are you sure you want to delete the budget',
      form: {
        newBudget: 'New Budget',
        editBudget: 'Edit Budget',
        budgetName: 'Budget name',
        budgetNamePlaceholder: 'E.g. Monthly food, Transport...',
        category: 'Category (optional)',
        allExpenses: 'All expenses',
        limit: 'Limit',
        period: 'Period',
        alertThreshold: 'Alert when reaching',
        alertDescription: 'You will be notified when your spending reaches this percentage of the limit.',
        preview: 'Preview',
        save: 'Save',
        create: 'Create',
      },
    },
    categories: {
      title: 'Categories',
      subtitle: 'Manage your income and expense categories',
      loading: 'Loading categories...',
      error: 'Error loading categories',
      errorDesc: 'Could not load your categories. Please try again.',
      newCategory: 'New Category',
      myCategories: 'My Categories',
      systemCategories: 'System Categories',
      systemCategoriesDesc: 'These are default system categories and cannot be edited.',
      noCustomCategories: 'No custom categories',
      noCustomCategoriesDesc: 'Create custom categories to better organise your finances.',
      createFirst: 'Create first category',
      type: {
        income: 'Income',
        expense: 'Expense',
      },
      deductible: 'Deductible',
      confirmDelete: 'Delete Category',
      confirmDeleteMsg: 'Are you sure you want to delete the category',
      deleteWarning: 'Existing transactions with this category will be moved to "Other".',
      form: {
        newCategory: 'New Category',
        editCategory: 'Edit Category',
        categoryName: 'Category name',
        categoryNamePlaceholder: 'E.g. Food, Transport...',
        type: 'Type',
        icon: 'Icon',
        colour: 'Colour',
        preview: 'Preview',
        taxDeductible: 'Tax deductible',
        save: 'Save',
        create: 'Create',
      },
    },
    receipts: {
      title: 'Receipts',
      subtitle: 'View and manage your tax receipts',
      loading: 'Loading receipts...',
      error: 'Error loading receipts',
      errorDesc: 'Could not load your receipts. Please try again.',
      status: {
        all: 'All Status',
        processing: 'Processing',
        completed: 'Completed',
        failed: 'Failed',
        manual_review: 'Manual Review',
      },
      year: {
        all: 'All Years',
      },
      stats: {
        total: 'Total',
        completed: 'Completed',
        processing: 'Processing',
        failed: 'Failed',
      },
      noReceipts: 'No receipts yet',
      noReceiptsFiltered: 'No receipts found',
      noReceiptsDesc: 'Send a receipt photo via WhatsApp to start organising your expenses',
      noReceiptsFilteredDesc: 'Try adjusting the filters to see more receipts',
      showing: 'Showing {count} of {total} receipts',
      page: 'Page {current} of {total}',
    },
    recurring: {
      title: 'Recurring Transactions',
      subtitle: 'Automate your fixed expenses and income',
      loading: 'Loading recurring transactions...',
      error: 'Error loading recurring transactions',
      errorDesc: 'Could not load your recurring transactions. Please try again.',
      newRecurring: 'New Recurring',
      noRecurring: 'No recurring transactions',
      noRecurringDesc: 'Set up transactions that repeat automatically, like rent, subscriptions, or salary.',
      createFirst: 'Create first recurring',
      active: 'Active',
      paused: 'Paused',
      activeCount: 'Active ({count})',
      pausedCount: 'Paused ({count})',
      next: 'Next',
      summary: {
        monthlyExpenses: 'Monthly Expenses',
        monthlyIncome: 'Monthly Income',
        netMonthly: 'Net Monthly',
      },
      frequency: {
        daily: 'Daily',
        weekly: 'Weekly',
        monthly: 'Monthly',
        yearly: 'Yearly',
      },
      dayOf: {
        everyDay: 'Every day',
        every: 'Every',
        dayOfMonth: 'Day {day}',
        annually: 'Annually',
      },
      actions: {
        pause: 'Pause',
        activate: 'Activate',
        processNow: 'Process now',
      },
      tips: {
        title: 'About Recurring Transactions',
        tip1: 'Transactions are created automatically on the scheduled date',
        tip2: 'You can pause temporarily without deleting',
        tip3: 'Use for fixed expenses like rent, electricity, internet',
        tip4: 'Also works for income like salary or rental income',
      },
      confirmDelete: 'Delete Recurring Transaction',
      confirmDeleteMsg: 'Are you sure you want to delete the recurring transaction',
      deleteWarning: 'Already created transactions will not be affected.',
      form: {
        newRecurring: 'New Recurring Transaction',
        editRecurring: 'Edit Recurring Transaction',
        description: 'Description',
        descriptionPlaceholder: 'E.g. Rent, Netflix, Salary...',
        amount: 'Amount',
        category: 'Category',
        selectCategory: 'Select a category',
        selectPot: 'Select a pot (optional)',
        frequencyLabel: 'Frequency',
        dayOfMonth: 'Day of month',
        dayOfWeek: 'Day of week',
        day: 'Day',
        startDate: 'Start date',
        endDate: 'End date (optional)',
        preview: 'Preview',
        save: 'Save',
        create: 'Create',
        daysOfWeek: {
          monday: 'Monday',
          tuesday: 'Tuesday',
          wednesday: 'Wednesday',
          thursday: 'Thursday',
          friday: 'Friday',
          saturday: 'Saturday',
          sunday: 'Sunday',
        },
      },
    },
    reports: {
      title: 'Tax Reports',
      subtitle: 'Annual summaries for tax returns',
      loading: 'Loading reports...',
      error: 'Error loading reports',
      errorDesc: 'Could not load your reports. Please try again.',
      year: {
        all: 'All years',
      },
      noReports: 'No reports yet',
      noReportsFiltered: 'No reports found',
      noReportsDesc: 'Your tax reports will appear here when automatically generated at the end of each year.',
      noReportsFilteredDesc: 'There are no reports for the year {year}.',
      generatedOn: 'Generated on',
      card: {
        income: 'Income',
        expenses: 'Expenses',
        deductible: 'Deductible',
        balance: 'Balance',
      },
      country: {
        BR: 'Brazil',
        GB: 'UK',
      },
      tips: {
        title: 'About Tax Reports',
        desc: 'Reports are automatically generated at the end of each tax year. They include a summary of all your income, expenses, and deductible spending, organised by category.',
        tip1: 'Brazil: Tax year from January to December',
        tip2: 'UK: Tax year from April to March',
      },
    },
  },

  nav: {
    pricing: 'Pricing',
    features: 'Features',
    howItWorks: 'How it Works',
    login: 'Login',
  },

  hero: {
    badge: 'Frictionless money management',
    title: 'Manage your finances via WhatsApp',
    subtitle: 'Send a message, photo, or voice note. AI logs, categorises, and organises everything for you.',
    cta: 'Start Free',
    ctaSubtext: '14 days free, no card required',
    ukOnly: 'Currently available for UK phone numbers only',
    trustedBy: 'As simple as sending a message',
  },

  howItWorks: {
    title: 'How it Works',
    subtitle: 'Three ways to log. Zero effort.',
    step1: {
      icon: '💬',
      title: 'Text',
      description: 'Type naturally: "50 lunch", "+500 salary", "yesterday uber was work"',
      example: '"85 groceries"',
    },
    step2: {
      icon: '📸',
      title: 'Photo',
      description: 'Send a receipt photo. OCR extracts amount, merchant, VAT and items automatically.',
      example: 'Photo of receipt',
    },
    step3: {
      icon: '🎤',
      title: 'Voice',
      description: 'Record a voice note describing the expense. We transcribe and log it.',
      example: '"Spent 40 on petrol"',
    },
  },

  features: {
    title: 'Much more than expense tracking',
    subtitle: 'Powerful tools, simple interface',
    list: [
      {
        icon: 'wallet',
        title: 'Pots (Wallets)',
        description: 'Organise by account: Monzo, Starling, Project X. Each pot with its own currency.',
        command: '"create pot Monzo"',
      },
      {
        icon: 'target',
        title: 'Budgets',
        description: 'Set limits by category. Get alerts at 80%.',
        command: '"budget 500 food"',
      },
      {
        icon: 'hash',
        title: 'Tags',
        description: 'Use #hashtags to organise: "50 uber #client #project-x"',
        command: '"my expenses #work"',
      },
      {
        icon: 'repeat',
        title: 'Recurring',
        description: 'Set up fixed expenses. Rent, Netflix, salary - logged automatically.',
        command: '"create recurring 1500 rent"',
      },
      {
        icon: 'globe',
        title: 'Multi-currency',
        description: '£, $, €, R$. Live exchange rates from ECB. Consolidated dashboard.',
        command: '"50 usd lunch pot Travel"',
      },
      {
        icon: 'layout',
        title: 'Web Dashboard',
        description: 'Charts, filters, exports. Everything you log, visible on the portal.',
        command: 'keepnota.com',
      },
    ],
  },

  commands: {
    title: 'Quick Commands',
    subtitle: 'Natural conversation. Nothing to memorise.',
    list: [
      { cmd: '50 coffee', desc: 'Logs £50 expense' },
      { cmd: '+2000 freelance', desc: 'Logs income' },
      { cmd: 'summary', desc: 'Shows monthly summary' },
      { cmd: 'my pots', desc: 'Lists your wallets' },
      { cmd: 'delete last', desc: 'Removes last transaction' },
      { cmd: 'help', desc: 'Lists all commands' },
    ],
  },

  audience: {
    title: 'Built for those who want simplicity',
    subtitle: 'If you use WhatsApp, you already know how to use KeepNota',
    personas: [
      {
        icon: 'briefcase',
        title: 'Freelancers & Self-Employed',
        description: 'Separate personal from business. Know exactly what comes in and out.',
      },
      {
        icon: 'user',
        title: 'Personal Finance',
        description: 'No more spreadsheets. Log expenses the moment they happen.',
      },
      {
        icon: 'users',
        title: 'Couples & Families',
        description: 'Shared pots. Everyone logs, everyone tracks.',
      },
      {
        icon: 'plane',
        title: 'Travellers',
        description: 'Automatic multi-currency. Expenses anywhere, all converted.',
      },
    ],
  },

  comparison: {
    title: 'Why KeepNota?',
    subtitle: 'Compared to alternatives',
    vsSpreadsheet: {
      title: 'vs Spreadsheet',
      points: [
        'No computer needed',
        'Instant logging',
        'Auto-categorises',
        'Receipt photo = everything filled',
      ],
    },
    vsApps: {
      title: 'vs Finance Apps',
      points: [
        'Works on WhatsApp (already use daily)',
        'Zero friction - just send a message',
        'Receipt OCR included',
        'No annoying notifications',
      ],
    },
    vsManual: {
      title: 'vs Manual Notes',
      points: [
        'Never forget to log',
        'Voice when you can\'t type',
        'History always accessible',
        'Automatic reports',
      ],
    },
  },

  taxBonus: {
    title: 'Bonus: Ready for Self Assessment',
    subtitle: 'When tax season comes, your data is already organised',
    points: [
      'Categories mapped to HMRC deductions',
      'VAT captured automatically from receipts',
      'Exportable annual report',
      'Separate deductible expenses with tags',
    ],
  },

  pricing: {
    title: 'Pricing',
    subtitle: 'Start free. Upgrade when you need.',
    monthly: '/month',
    yearly: '/year',
    savePercent: 'Save 20%',
    basic: {
      name: 'Basic',
      price: '4.99',
      features: [
        '30 receipt photos/month',
        '20 voice notes/month',
        'Unlimited text messages',
        'Dashboard & basic charts',
        'Monthly report',
        'Export CSV',
        'Receipt retention: 1 year',
      ],
    },
    pro: {
      name: 'Pro',
      price: '9.99',
      popular: 'Most Popular',
      features: [
        'Unlimited photos & audio',
        'Everything in Basic +',
        'Advanced charts & filters',
        'Month-to-month comparison',
        'Export PDF',
        'Annual tax report',
        'Priority support',
        'Receipt retention: 2 years',
      ],
    },
    cta: 'Start Free',
    trial: '14-day free trial, cancel anytime',
  },

  referral: {
    title: 'Refer & Earn',
    subtitle: 'Share with friends and earn free days',
    tiers: [
      { range: '1-5 referrals', reward: '7 free days each' },
      { range: '6-15 referrals', reward: '10 free days each' },
      { range: '16+ referrals', reward: '14 days + credits' },
    ],
    cta: 'Learn more',
  },

  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        q: 'Do I need to download an app?',
        a: 'No! It works 100% via WhatsApp. Just save our number and start sending your expenses. We also have a web portal for charts and reports.',
      },
      {
        q: 'Is my data secure?',
        a: 'Yes! We use end-to-end encryption, secure servers, and comply with GDPR. You can export or delete your data at any time.',
      },
      {
        q: 'Can I cancel anytime?',
        a: 'Of course! No commitment. Cancel anytime via WhatsApp by typing "cancel". Your data remains available for 30 days after cancellation.',
      },
      {
        q: 'Does it work for freelancers and self-employed?',
        a: 'Perfect for that! Use pots to separate personal from business, tags for clients, and have full control of income and expenses.',
      },
      {
        q: 'How does receipt OCR work?',
        a: 'Send a photo of your receipt. Our AI automatically extracts: total amount, merchant name, VAT, date, and even line items. Just confirm and done.',
      },
      {
        q: 'Can I use multiple currencies?',
        a: 'Yes! We support Pounds, Dollars, Euros, and Brazilian Real. Each pot can have its own currency, and the dashboard shows everything converted to your main currency.',
      },
    ],
  },

  cta: {
    title: 'Start organising your finances today',
    subtitle: '14 days free. No card. Cancel anytime.',
    button: 'Chat on WhatsApp',
    secondary: 'See demo',
  },

  footer: {
    copyright: '© 2025 KeepNota by Fig and Rosa Ltd. All rights reserved.',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    tagline: 'Financial control via WhatsApp',
  },

  privacy: {
    title: 'Privacy Policy',
    lastUpdate: 'Last updated: December 2024',
    intro: 'KeepNota ("we", "our" or "Service") values user privacy. This Privacy Policy explains how we collect, use and protect your information.',
    sections: [
      {
        title: '1. Data We Collect',
        content: `We collect the following data when you use KeepNota:
• **Phone number**: For identification and WhatsApp communication
• **Messages sent**: Texts, voice notes and receipt images you send
• **Extracted data**: Amounts, categories and information from processed receipts
• **Usage data**: Access times and service interactions`,
      },
      {
        title: '2. How We Use Your Data',
        content: `We use your data to:
• Process and organise your receipts
• Generate expense reports and charts
• Identify tax-deductible expenses
• Improve our services and AI algorithms
• Send communications about your account and the service`,
      },
      {
        title: '3. Storage and Security',
        content: `Your data is stored securely:
• Encryption in transit (HTTPS/TLS)
• Secure server storage
• Access restricted to automated systems only
• Regular backups and data redundancy`,
      },
      {
        title: '4. Data Sharing',
        content: `**We do not sell your data.** We only share with:
• **Service providers**: Payment processing (Stripe), storage (AWS)
• **AI/ML**: Google (Gemini) for image and text processing - anonymised data
• **Legal obligation**: When required by law or court order`,
      },
      {
        title: '5. Your Rights (GDPR)',
        content: `You have the right to:
• **Access**: Request a copy of your data
• **Rectification**: Correct inaccurate data
• **Erasure**: Request deletion of your data
• **Portability**: Receive your data in a structured format
• **Withdraw consent**: Cancel consent at any time

To exercise your rights, send a message to our WhatsApp or email: privacy@keepnota.com`,
      },
      {
        title: '6. Data Retention',
        content: `We keep your data while your account is active. After cancellation:
• Financial data: Kept for 5 years (tax obligation)
• Receipt images: Deleted within 30 days
• Account data: Deleted within 30 days`,
      },
      {
        title: '7. Contact',
        content: `For privacy enquiries:
• Email: privacy@keepnota.com
• WhatsApp: Send "PRIVACY" to our number`,
      },
    ],
  },

  feedback: {
    title: 'Send your Feedback',
    subtitle: 'Your opinion helps us improve KeepNota',
    typeLabel: 'Type of feedback',
    types: {
      general: 'General',
      feature_request: 'Suggestion',
      bug: 'Bug/Error',
      complaint: 'Complaint',
      praise: 'Praise',
      account_deletion: 'Delete account',
    },
    messageLabel: 'Your message',
    messagePlaceholder: 'Tell us what you think...',
    ratingLabel: 'Rating (optional)',
    nameLabel: 'Your name (optional)',
    namePlaceholder: 'What should we call you?',
    emailLabel: 'Email (optional)',
    emailPlaceholder: 'your@email.com',
    emailHint: 'So we can respond to your feedback',
    submit: 'Send Feedback',
    errors: {
      messageRequired: 'Please write a message',
      messageTooShort: 'Message too short (minimum 10 characters)',
      submitFailed: 'Failed to send. Please try again.',
    },
    success: {
      title: 'Feedback sent!',
      message: 'Thank you for sharing your thoughts. We will review it carefully.',
    },
    backToHome: 'Back to homepage',
  },

  terms: {
    title: 'Terms of Service',
    lastUpdate: 'Last updated: December 2024',
    intro: 'By using KeepNota, you agree to these Terms of Service. Please read carefully.',
    sections: [
      {
        title: '1. About the Service',
        content: `KeepNota is a receipt and expense organisation service via WhatsApp. We offer:
• Automatic receipt processing via photo, audio or text
• Expense categorisation and organisation
• Spending reports and charts
• Tax-deductible expense identification

The service is provided "as is" and may be modified at any time.`,
      },
      {
        title: '2. Registration and Account',
        content: `To use KeepNota:
• You must be 18 years or older
• Provide accurate information
• Keep your phone number up to date
• Not share access with third parties

You are responsible for all activity on your account.`,
      },
      {
        title: '3. Acceptable Use',
        content: `You agree NOT to:
• Use the service for illegal purposes
• Send offensive, defamatory or illegal content
• Attempt to access other users' systems or data
• Use unauthorised bots or automation
• Resell or redistribute the service`,
      },
      {
        title: '4. Payments and Subscription',
        content: `• We offer a 14-day free trial period
• After the trial, subscription is billed monthly
• Payments processed via Stripe (credit card)
• Cancellation can be done at any time
• No refunds for partial periods`,
      },
      {
        title: '5. Intellectual Property',
        content: `• KeepNota and its components are property of Figrosa
• You retain ownership of your data and receipts
• We grant you a limited licence to use the service
• You grant us licence to process your data as necessary`,
      },
      {
        title: '6. Limitation of Liability',
        content: `• KeepNota does not replace accounting or tax advice
• We do not guarantee absolute accuracy in receipt processing
• We are not responsible for tax decisions based on the service
• Our liability is limited to the amount paid for the service`,
      },
      {
        title: '7. Cancellation',
        content: `You can cancel at any time:
• Send "CANCEL" via WhatsApp
• Access your account on the portal
• Access continues until the end of the paid period
• Your data will be deleted per our Privacy Policy`,
      },
      {
        title: '8. Changes to Terms',
        content: `We may change these terms at any time. We will notify via:
• WhatsApp message
• Email (if registered)
• Portal notice

Continued use after changes constitutes acceptance.`,
      },
      {
        title: '9. Contact',
        content: `For questions about the Terms:
• Email: support@keepnota.com
• WhatsApp: Send "HELP" to our number`,
      },
    ],
  },
};
